import { createApp } from 'vue'
import { createPinia } from 'pinia'
import '@aws-amplify/ui-vue/styles.css'

import App from './App.vue'
import router from './router'

// Import Vuetify
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { mdi } from 'vuetify/iconsets/mdi'
import '@mdi/font/css/materialdesignicons.css'

// Import our modern theme system
import { themeConfig } from '@/styles/vuetify-theme'
import '@/styles/global-theme.css'
import '@/styles/legacy-theme.css'

// Import tenant configuration services
import { tenantConfigService } from '@/services/tenantConfigService'
import { amplifyConfigManager } from '@/services/amplifyConfigManager'
import { Hub, I18n } from 'aws-amplify/utils'
import { useAuthStore } from '@/stores/auth'
import { installStaleAssetRecovery } from '@/utils/staleAssetRecovery'
import { installSessionExpiryHandler } from '@/utils/sessionExpiry'
import { renderMaintenanceScreen } from '@/utils/maintenanceScreen'
import { useAppUpdate } from '@/composables/useAppUpdate'
// Enable Amplify's OAuth redirect listener on every page load. The SSO redirect returns to '/'
// via a full page reload, so the listener registered as a side-effect of signInWithRedirect on
// the login page is gone; without this explicit import the code→token exchange never completes on
// the landing page (the side-effect is also dropped from the optimized production bundle).
import 'aws-amplify/auth/enable-oauth-listener'

// Initialize tenant configuration
async function initializeTenant() {
  try {
    const config = await tenantConfigService.getTenantConfig()
    await amplifyConfigManager.configureTenant(config)
  } catch (error) {
    console.error('Failed to initialize tenant configuration:', error)
    throw error
  }
}

const prefersDark = window.matchMedia('(prefers-color-scheme: dark)')

const vuetify = createVuetify({
  components,
  directives,
  icons: {
    defaultSet: 'mdi',
    sets: { mdi }
  },
  theme: {
    ...themeConfig,
    defaultTheme: prefersDark.matches ? 'dark' : 'light'
  }
})

prefersDark.addEventListener('change', (e) => {
  vuetify.theme.global.name.value = e.matches ? 'dark' : 'light'
})

// The password sign-in counterpart to resolveSSOError below. auth-pre-login aborts a login by
// raising, and Cognito hands the Authenticator the raw "PreAuthentication failed with error <msg>."
// — an internal trigger name in front of the sentence the user is meant to read. Amplify's I18n is
// an EXACT-STRING lookup with no pattern support, so every message auth-pre-login can raise needs
// its own key, spelled exactly as the Lambda raises it plus the period Cognito appends. Anything
// unlisted still renders with the prefix, so keep this in step with
// amplify/functions/auth-pre-login/index.py.
const PRE_AUTH_PREFIX = 'PreAuthentication failed with error '
const preAuthMessages: Record<string, string> = {
  // gen1 App.ts carried this one translation (SI#458310); its wording is preserved verbatim.
  'Your password has been reset and we have emailed you instructions on how to access it':
    'Your password has expired. Check your email for instructions on how to set a new one.',
  'Please contact your facility Administrator to finalize setup':
    'Please contact your facility Administrator to finalize setup.',
  'Facility is not active':
    'This facility is not currently active. Please contact your administrator.',
  'Evaluation facility is not active':
    "This facility's evaluation period is not active. Please contact your administrator.",
  'Evaluation facility has no start and end dates':
    "This facility's evaluation period is not active. Please contact your administrator.",
  'User not found or inactive':
    'User account not found. Please contact your administrator.',
}
I18n.putVocabularies({
  en: Object.fromEntries(
    Object.entries(preAuthMessages).map(([raw, friendly]) => [`${PRE_AUTH_PREFIX}${raw}.`, friendly]),
  ),
})

// A Cognito auth trigger aborts a federated sign-in by throwing, and the thrown message is the
// only thing that survives the OAuth error redirect — there is no error-code channel. So the
// deliberate aborts have to be recognised by their text and translated into something a user can
// act on; anything unrecognised falls through unchanged.
function resolveSSOError(message: string): string {
  // auth-pre-signup linked the federated identity into the user's existing Cognito account and
  // aborted so Cognito wouldn't also create a duplicate. Retrying signs them in.
  if (message.includes('Account linked')) {
    return 'Your account has been linked. Please sign in again to continue.'
  }
  // auth-pre-signup refused to create a profile because no portal user is provisioned for this
  // person yet (commonly a directory email that doesn't match, or SCIM hasn't synced them).
  if (message.includes('User account not found')) {
    return 'User account not found. Please contact your administrator.'
  }
  return message || 'SSO sign-in failed. Please try again.'
}

// The boot screen lives outside #app in index.html so it survives app.mount() and keeps covering
// the router's first navigation — which is where a cold load actually stalls, on the token refresh
// in authGuard.
function dismissBootLoader() {
  const boot = document.getElementById('tc-boot')
  if (!boot) return

  boot.classList.add('tc-boot--done')
  setTimeout(() => boot.remove(), 400)
}

// Initialize app with tenant configuration
async function initializeApp() {
  try {
    // Registered before the first await so a route chunk left stale by a deploy is recovered
    // from even if it fails while the tenant config is still loading.
    installStaleAssetRecovery(router, () => useAppUpdate().requireReload())

    // Initialize tenant configuration (calls Amplify.configure). With the enable-oauth-listener
    // import above, Amplify completes the redirect code→token exchange once configured.
    await initializeTenant()

    // Create and configure the Vue app up front so Pinia (for useAuthStore) and the router are
    // available to the auth Hub listener below. Mounting still happens last, after Amplify is
    // configured.
    const app = createApp(App)

    app.use(createPinia())
    app.use(router)
    app.use(vuetify)

    // Expired-session handling (tokenRefresh_failure) lives in its own module — see
    // utils/sessionExpiry. Registered after Pinia so its listener can resolve the auth store.
    installSessionExpiryHandler(router)

    // Global auth events. The SSO/federated redirect returns to '/', not '/login', so both the
    // success and failure of the redirect must be handled here (not just on the login page). The
    // code→token exchange is a network round-trip, so this listener — registered synchronously
    // right after Amplify.configure — is always in place before the signInWithRedirect success
    // event can fire.
    Hub.listen('auth', async ({ payload }) => {
      const authStore = useAuthStore()
      if (payload.event === 'signInWithRedirect') {
        // The federated code→token exchange completed. Populate the user and leave the
        // redirect-landing route so the app bootstraps instead of sitting blank at '/?code='.
        await authStore.fetchUser()
        router.push('/home')
      } else if (payload.event === 'signInWithRedirect_failure') {
        const message =
          (payload as { data?: { error?: { message?: string } } }).data?.error?.message || ''
        authStore.error = resolveSSOError(message)
        router.push('/login')
      }
    })

    // Mount the app
    app.mount('#app')

    // Not awaited, and errors are swallowed: keeping this out of the control flow means a failing
    // initial navigation still dismisses the boot screen instead of falling into the catch below,
    // which would replace the whole body with the configuration-error screen.
    router.isReady().catch(() => {}).then(dismissBootLoader)
  } catch (error) {
    // The only diagnostic channel left once the body is replaced — a withheld config.json and a
    // genuinely broken one both land here and look identical to the user.
    console.error('Failed to initialize application:', error)
    renderMaintenanceScreen()
  }
}

// Start the application
initializeApp()
